import { COMMUNITY_PURPOSES } from "../lib/communityParticipation";
import { CommunityFeedRow } from "../components/community/CommunityFeedRow";
import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search,
  PenSquare,
  Pin,
  MessageSquareText,
  ArrowUpRight,
  RotateCcw,
  X,
  NotebookPen,
  ChevronDown,
  CircleHelp,
  FolderHeart,
  HeartHandshake,
  Sparkles,
  BookOpen,
  Code2,
  Image,
} from "lucide-react";
import { WorkspaceHeader } from "../components/ui/WorkspaceHeader";
import { apiClient, type CommunityCategory } from "../lib/api";
import {
  listCommunityBoardFeed,
  type CommunityBoardFeedCursor,
  type CommunityBoardFeedItem,
} from "../lib/communityBoardFeed";
import { mergeUniqueItems, readBoardFilters } from "../lib/communityBoardState";
import { fillCommunityStories } from "../lib/communityStoryPage";
import { useAuth } from "../contexts/AuthContext";
import { usePageSEO } from "../lib/usePageSEO";
import "../styles/community-renewal.css";

function itemKey(item: CommunityBoardFeedItem) {
  return `${item.kind}-${item.id}`;
}

export function CommunityBoard() {
  const { isAuthenticated } = useAuth();
  const [params, setParams] = useSearchParams();
  const {
    categoryId,
    subcategoryId,
    sort,
    q,
    tag,
    author,
    purpose,
    unanswered,
    news,
  } = readBoardFilters(params);
  const [search, setSearch] = React.useState(q);
  const [categories, setCategories] = React.useState<CommunityCategory[]>([]);
  const [categoryError, setCategoryError] = React.useState(false);
  const [items, setItems] = React.useState<CommunityBoardFeedItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [cursor, setCursor] = React.useState<CommunityBoardFeedCursor | null>(
    null,
  );
  const [hasMore, setHasMore] = React.useState(false);
  const [visibleStoryCount, setVisibleStoryCount] = React.useState(10);
  const [showAllNotices, setShowAllNotices] = React.useState(false);
  const [retry, setRetry] = React.useState(0);
  const requestVersion = React.useRef(0);
  const morePending = React.useRef(false);

  usePageSEO({
    title: "교사 커뮤니티 게시판 | 소통공간 - Foreducator",
    description:
      "교사들이 수업·업무·학급 이야기를 나누는 게시판입니다. 로그인 없이 글을 읽고, 가입 후 자료를 공유하세요.",
    path: "/community/board",
    keywords: "교사 커뮤니티, 교사 게시판, 교육 자료 공유, 소통공간",
    ogImage: "/og/2026/community.png",
  });
  React.useEffect(() => {
    setSearch(q);
  }, [q]);
  React.useEffect(() => {
    let active = true;
    setCategoryError(false);
    apiClient
      .listCommunityCategories()
      .then((data) => {
        if (active) setCategories(data);
      })
      .catch(() => {
        if (active) setCategoryError(true);
      });
    return () => {
      active = false;
    };
  }, [retry]);

  const feedParams = React.useMemo(
    () => ({
      limit: 20,
      purpose,
      unanswered,
      news:
        news === "only"
          ? ("only" as const)
          : news === "exclude" || !!purpose
            ? ("exclude" as const)
            : undefined,
      category_id: categoryId,
      subcategory_id: subcategoryId,
      sort,
      q,
      tag,
      author: author || undefined,
    }),
    [
      categoryId,
      subcategoryId,
      sort,
      q,
      tag,
      author,
      purpose,
      unanswered,
      news,
    ],
  );
  React.useEffect(() => {
    const version = ++requestVersion.current;
    morePending.current = false;
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    setItems([]);
    setVisibleStoryCount(10);
    setShowAllNotices(false);
    setCursor(null);
    setHasMore(false);
    fillCommunityStories(
      (cursor) => listCommunityBoardFeed(feedParams, cursor),
      [],
      null,
      10,
      () => version === requestVersion.current,
    )
      .then((result) => {
        if (version !== requestVersion.current) return;
        setItems(mergeUniqueItems([], result.results, itemKey));
        setCursor(result.cursor);
        setHasMore(result.hasMore);
      })
      .catch((e: unknown) => {
        if (version === requestVersion.current)
          setError(
            e instanceof Error ? e.message : "이야기를 불러오지 못했어요.",
          );
      })
      .finally(() => {
        if (version === requestVersion.current) setLoading(false);
      });
    return () => {
      requestVersion.current += 1;
    };
  }, [feedParams, retry]);

  const loadMore = async () => {
    if (
      stories.length >= visibleStoryCount + 10 ||
      (!hasMore && stories.length > visibleStoryCount)
    ) {
      setVisibleStoryCount((count) => count + 10);
      return;
    }
    if (loading || morePending.current || !hasMore || !cursor) return;
    const version = requestVersion.current;
    morePending.current = true;
    setLoadingMore(true);
    setError(null);
    try {
      const result = await fillCommunityStories(
        (nextCursor) => listCommunityBoardFeed(feedParams, nextCursor),
        items,
        cursor,
        visibleStoryCount + 10,
        () => version === requestVersion.current,
      );
      if (version !== requestVersion.current) return;
      setItems((previous) =>
        mergeUniqueItems(previous, result.results, itemKey),
      );
      setCursor(result.cursor);
      setHasMore(result.hasMore);
      setVisibleStoryCount((count) => count + 10);
    } catch (e: unknown) {
      if (version === requestVersion.current)
        setError(
          e instanceof Error ? e.message : "다음 이야기를 불러오지 못했어요.",
        );
    } finally {
      if (version === requestVersion.current) {
        morePending.current = false;
        setLoadingMore(false);
      }
    }
  };
  const updateFilters = (changes: Record<string, string | number>) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === "" || (key === "sort" && value === "recent"))
          next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    });
  };
  const reset = () => {
    setSearch("");
    setParams({});
  };
  const subcategories =
    categories.find((c) => c.id === categoryId)?.subcategories || [];
  const notices = items.filter(
    (item) => item.kind === "post" && item.post.top_fixed,
  );
  const stories = items;
  const visibleStories = stories.slice(0, visibleStoryCount);
  const filtered = Boolean(
    categoryId ||
    subcategoryId ||
    q ||
    tag ||
    author ||
    purpose ||
    unanswered ||
    news,
  );
  const categoryName =
    (news === "only"
      ? "교육뉴스"
      : news === "exclude"
        ? "선생님 이야기"
        : unanswered
          ? "첫 답변을 기다리는 질문"
          : COMMUNITY_PURPOSES.find((p) => p.id === purpose)?.label) ||
    categories.find((c) => c.id === categoryId)?.name ||
    "전체 이야기";
  const newPostParams = new URLSearchParams();
  if (purpose) newPostParams.set("purpose", purpose);
  if (categoryId) newPostParams.set("category_id", String(categoryId));
  if (subcategoryId) newPostParams.set("subcategory_id", String(subcategoryId));
  const newPostPath = `/community/board/new${newPostParams.size ? `?${newPostParams}` : ""}`;

  return (
    <div className="fe-page-canvas fe-community fe-community-board">
      <WorkspaceHeader
        title="소통공간"
        eyebrow="BETTER TOGETHER"
        description="막힌 일은 함께 풀고, 만든 것은 함께 써요. 선생님의 경험이 동료의 다음 수업이 됩니다."
        icon={MessageSquareText}
        tone="peach"
      />
      <div className="fe-community-layout">
        <div className="fe-community-topline">
          <div>
            <h2>
              선생님 곁의 열린 교무실<span className="fe-heading-dot">.</span>
            </h2>
            <p>
              질문 하나, 자료 하나, 직접 만든 것 하나. 여기서 이야기를 시작해요.
            </p>
          </div>
          <div className="fe-community-actions">
            {isAuthenticated && (
              <Link to="/community/my" className="fe-community-button">
                <NotebookPen size={16} />내 활동
              </Link>
            )}
            <Link
              to={
                isAuthenticated
                  ? newPostPath
                  : `/login?next=${encodeURIComponent(newPostPath)}`
              }
              className="fe-button fe-button-dark"
            >
              <PenSquare size={16} />
              글쓰기
            </Link>
          </div>
        </div>
        <section
          className="fe-community-launchpad"
          aria-label="이야기 시작하기"
        >
          {COMMUNITY_PURPOSES.map((item, i) => {
            const Icon = [CircleHelp, FolderHeart, HeartHandshake, Sparkles][i];
            const path = `/community/board/new?purpose=${item.id}`;
            return (
              <Link
                key={item.id}
                to={
                  isAuthenticated
                    ? path
                    : `/login?next=${encodeURIComponent(path)}`
                }
                className={`fe-community-start fe-community-start-${item.id}`}
              >
                <span className="fe-community-start-icon">
                  <Icon size={23} strokeWidth={1.7} />
                </span>
                <strong>
                  {item.label}
                  <ArrowUpRight size={16} />
                </strong>
                <span>{item.description}</span>
              </Link>
            );
          })}
        </section>
        <div className="fe-community-board-grid">
          <section
            className="fe-community-filters"
            aria-label="게시글 검색과 분류"
          >
            <div
              className="fe-community-purpose-tabs"
              role="group"
              aria-label="이야기 목적"
            >
              <button
                aria-pressed={!purpose && !news}
                onClick={() =>
                  updateFilters({
                    purpose: "",
                    unanswered: "",
                    news: "",
                    category_id: "",
                    subcategory_id: "",
                  })
                }
              >
                전체보기
              </button>
              <button
                aria-pressed={!purpose && news === "exclude"}
                onClick={() =>
                  updateFilters({
                    purpose: "",
                    unanswered: "",
                    news: "exclude",
                    category_id: "",
                    subcategory_id: "",
                  })
                }
              >
                선생님 이야기
              </button>
              {COMMUNITY_PURPOSES.map((item) => (
                <button
                  key={item.id}
                  aria-pressed={purpose === item.id}
                  onClick={() =>
                    updateFilters({
                      purpose: item.id,
                      unanswered: "",
                      news: "",
                    })
                  }
                >
                  {item.label}
                </button>
              ))}
              <button
                aria-pressed={news === "only"}
                onClick={() =>
                  updateFilters({
                    purpose: "",
                    unanswered: "",
                    news: "only",
                    category_id: "",
                    subcategory_id: "",
                  })
                }
              >
                교육뉴스
              </button>
            </div>
            {purpose === "question" && (
              <label className="fe-community-awaiting">
                <input
                  type="checkbox"
                  checked={unanswered}
                  onChange={(e) =>
                    updateFilters({ unanswered: e.target.checked ? "1" : "" })
                  }
                />{" "}
                첫 댓글이 아직 없는 질문만 보기
              </label>
            )}
            {purpose === "showcase" && (
              <div className="fe-community-creator-links">
                <span>책과 웹사이트도 함께 만나보세요</span>
                <Link to="/informate/books">
                  교사들의 책 <ArrowUpRight size={14} />
                </Link>
                <Link to="/informate/vibe-sites">
                  바이브코딩 <ArrowUpRight size={14} />
                </Link>
              </div>
            )}
            <details
              className="fe-community-topics"
              open={categoryId ? true : undefined}
            >
              <summary>
                주제로 좁혀 보기
                {categoryId
                  ? ` · ${categories.find((c) => c.id === categoryId)?.name}`
                  : " · 수업 / 업무 / 에듀테크"}
                <ChevronDown size={14} />
              </summary>
              <div
                className="fe-community-tabs"
                role="group"
                aria-label="카테고리"
              >
                <button
                  aria-pressed={!categoryId}
                  onClick={() =>
                    updateFilters({ category_id: "", subcategory_id: "" })
                  }
                >
                  전체
                </button>
                {categories.map((c) => (
                  <button
                    key={c.id}
                    aria-pressed={categoryId === c.id}
                    onClick={() =>
                      updateFilters({ category_id: c.id, subcategory_id: "" })
                    }
                  >
                    {c.name}
                  </button>
                ))}
              </div>
              {subcategories.length > 0 && (
                <div
                  className="fe-community-subtabs"
                  role="group"
                  aria-label="세부 카테고리"
                >
                  <button
                    aria-pressed={!subcategoryId}
                    onClick={() => updateFilters({ subcategory_id: "" })}
                  >
                    모두 보기
                  </button>
                  {subcategories.map((s) => (
                    <button
                      key={s.id}
                      aria-pressed={subcategoryId === s.id}
                      onClick={() => updateFilters({ subcategory_id: s.id })}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
            </details>
            {categoryError && (
              <p className="fe-community-inline-error" role="alert">
                분류를 불러오지 못했어요.{" "}
                <button onClick={() => setRetry((n) => n + 1)}>
                  다시 시도
                </button>
              </p>
            )}
            <div className="fe-community-searchbar">
              <form
                role="search"
                className="fe-community-search"
                onSubmit={(event) => {
                  event.preventDefault();
                  updateFilters({ q: search.trim() });
                }}
              >
                <Search size={18} aria-hidden="true" />
                <input
                  aria-label="게시글 검색"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="궁금한 일, 필요한 자료, 도구 이름 검색"
                />
                {search && (
                  <button
                    type="button"
                    className="fe-community-clear"
                    aria-label="검색어 지우기"
                    onClick={() => {
                      setSearch("");
                      updateFilters({ q: "" });
                    }}
                  >
                    <X size={16} />
                  </button>
                )}
                <button type="submit" className="fe-community-search-submit">
                  검색
                </button>
              </form>
              <label className="fe-community-sort">
                <span className="sr-only">게시글 정렬</span>
                <select
                  value={sort}
                  onChange={(e) => updateFilters({ sort: e.target.value })}
                >
                  <option value="recent">최신순</option>
                  <option value="popular">인기순</option>
                  <option value="hits">조회순</option>
                  <option value="likes">좋아요순</option>
                </select>
                <ChevronDown size={15} aria-hidden="true" />
              </label>
            </div>
            {filtered && (
              <div className="fe-community-filter-summary">
                <span>{q ? `“${q}” 검색 결과` : categoryName}</span>
                {tag && (
                  <button onClick={() => updateFilters({ tag: "" })}>
                    #{tag}
                    <X size={13} />
                    <span className="sr-only">태그 필터 해제</span>
                  </button>
                )}
                {author && (
                  <button onClick={() => updateFilters({ author: "" })}>
                    작성자 필터
                    <X size={13} />
                    <span className="sr-only">해제</span>
                  </button>
                )}
                <button onClick={reset} className="fe-community-reset">
                  <RotateCcw size={13} />
                  전체 초기화
                </button>
              </div>
            )}
          </section>

          <aside
            className="fe-community-sidebar"
            aria-label="공지와 소통공간 바로가기"
          >
            <div className="fe-community-creator-card">
              <h3>
                <Sparkles size={18} /> 선생님이 만드는 것들
              </h3>
              <p>
                책도, 포스터도, 작은 프로그램도. 만든 분의 이야기를 듣고 함께 발전시켜요.
              </p>
              <Link to="/informate/books">
                <BookOpen size={17} /> 교사들의 책 <ArrowUpRight size={14} />
              </Link>
              <Link to="/workmate/poster-studio">
                <Image size={17} /> 포스터 갤러리 <ArrowUpRight size={14} />
              </Link>
              <Link to="/informate/vibe-sites">
                <Code2 size={17} /> 바이브코딩 작품 <ArrowUpRight size={14} />
              </Link>
              <Link to="/community/board?purpose=showcase">
                창작 이야기 모아보기 <ArrowUpRight size={14} />
              </Link>
            </div>
            {!loading && (
              <>
                {notices.length > 0 && (
                  <details className="fe-community-notices" open>
                    <summary>
                      <span>
                        <Pin size={15} />
                        알려드려요 <small>{notices.length}</small>
                      </span>
                      <ChevronDown size={16} />
                    </summary>
                    <ul>
                      {(showAllNotices ? notices : notices.slice(0, 3)).map(
                        (item) => (
                          <CommunityFeedRow
                            key={itemKey(item)}
                            item={item}
                            notice
                          />
                        ),
                      )}
                    </ul>
                    {notices.length > 3 && (
                      <button
                        type="button"
                        className="fe-community-button mx-4 mb-4 mt-2"
                        aria-expanded={showAllNotices}
                        onClick={() => setShowAllNotices((value) => !value)}
                      >
                        {showAllNotices
                          ? "공지 접기"
                          : `공지 더 보기 (${notices.length - 3})`}
                      </button>
                    )}
                  </details>
                )}
              </>
            )}
            <div className="fe-community-side-links">
              <h3>
                <NotebookPen size={17} /> 나의 소통공간
              </h3>
              <p>나눈 이야기와 다시 보고 싶은 글을 모아두세요.</p>
              <Link
                to={
                  isAuthenticated
                    ? "/community/my"
                    : "/login?next=%2Fcommunity%2Fmy"
                }
              >
                내 글과 활동 <ArrowUpRight size={15} />
              </Link>
              <Link
                to={
                  isAuthenticated
                    ? "/community/my?tab=bookmarks"
                    : "/login?next=%2Fcommunity%2Fmy%3Ftab%3Dbookmarks"
                }
              >
                저장한 이야기 <ArrowUpRight size={15} />
              </Link>
              <Link to="/guide/community" className="fe-community-side-guide">
                소통공간 이용 가이드 <ArrowUpRight size={15} />
              </Link>
            </div>
          </aside>

          <section
            className="fe-community-feed"
            aria-label="게시글 목록"
            aria-busy={loading}
          >
            {loading ? (
              <div
                className="fe-community-skeleton"
                role="status"
                aria-label="게시글 불러오는 중"
              >
                {Array.from({ length: 5 }, (_, i) => (
                  <div key={i}>
                    <span />
                    <span />
                  </div>
                ))}
              </div>
            ) : (
              <>
                {stories.length > 0 && (
                  <>
                    <div className="fe-community-feed-heading">
                      <h3>{categoryName}</h3>
                      <span>현재 {visibleStories.length}개의 이야기</span>
                    </div>
                    <ul className="fe-community-stories">
                      {visibleStories.map((item) => (
                        <CommunityFeedRow key={itemKey(item)} item={item} />
                      ))}
                    </ul>
                  </>
                )}
                {!stories.length && !error && (
                  <div className="fe-community-empty">
                    <div className="fe-community-empty-icon">
                      <MessageSquareText size={28} />
                    </div>
                    <h3>
                      {filtered
                        ? "조건에 맞는 이야기가 없어요"
                        : "첫 이야기를 기다리고 있어요"}
                    </h3>
                    <p>
                      {filtered
                        ? "검색어를 짧게 바꾸거나 필터를 초기화해 보세요."
                        : "작은 질문 하나로 대화를 시작해 보세요."}
                    </p>
                    {filtered ? (
                      <button onClick={reset} className="fe-community-button">
                        검색 초기화
                      </button>
                    ) : (
                      <Link
                        to={
                          isAuthenticated
                            ? newPostPath
                            : `/login?next=${encodeURIComponent(newPostPath)}`
                        }
                        className="fe-community-button"
                      >
                        첫 글 쓰기
                        <ArrowUpRight size={15} />
                      </Link>
                    )}
                  </div>
                )}
              </>
            )}
            {error && (
              <div className="fe-community-error" role="alert">
                <p>{error}</p>
                <button
                  className="fe-community-button"
                  onClick={() => {
                    if (items.length && hasMore) void loadMore();
                    else setRetry((n) => n + 1);
                  }}
                >
                  다시 불러오기
                </button>
              </div>
            )}
            {!loading && (hasMore || stories.length > visibleStoryCount) && (
              <div className="fe-community-pagination">
                <button
                  className="fe-community-button"
                  onClick={() => void loadMore()}
                  disabled={loadingMore}
                >
                  {loadingMore ? "이야기를 불러오는 중…" : "이야기 더 보기"}
                  <ChevronDown size={16} />
                </button>
              </div>
            )}
            <span className="sr-only" role="status" aria-live="polite">
              {!loading &&
                `${visibleStories.length}개의 이야기를 표시하고 있습니다.`}
            </span>
          </section>
        </div>
      </div>
    </div>
  );
}
